import styled from 'styled-components';
import { Text } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import { FlexBox, Inline } from '@ardoq/layout';
import {
  ButtonSize,
  ButtonType,
  IconButton,
  SmallGhostButton,
} from '@ardoq/button';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { UnstyledLink } from '@ardoq/link';
import { getFocusRingBorderCSS } from '@ardoq/style-helpers';
import {
  Badge,
  InteractiveBadgeProps,
  ReadonlyBadgeProps,
} from '@ardoq/status-ui';
import { PropsReturningActions } from '@ardoq/rxbeach';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { useId, useState } from 'react';
import {
  DropdownContainer,
  DropdownLabel,
  DropdownLink,
  DropdownMaxHeight,
  DropdownMenu,
  DropdownOption,
  DropdownOptionContent,
  useDropdownTriggerContainer,
} from '@ardoq/dropdown-2026';
import { Popover2025 } from '@ardoq/popover2025';
import { NewPageTopBarActionButton } from './NewPageTopBarActionButton';
import {
  ActionMenuButton,
  ActionMenuDivider,
  ActionMenuDropdownButton,
  ActionMenuIconButton,
  ActionMenuItem,
  ActionMenuLinkButton,
  BreadcrumbItem,
  CommonActionMenuItemProps,
  HelpButtonItem,
  ToggleSidebarButtonProps,
  TrailingButton,
} from './types';

export const NEW_PAGE_TOP_BAR_HEIGHT = 50;

const TopBarWrapper = styled(FlexBox)`
  border-bottom: 1px solid ${colors.borderSubtle};
`;

const Right = styled(FlexBox)`
  flex: 1 1 auto;
  margin-left: auto;
`;

const BreadcrumbSeparator = styled(Text)`
  color: ${colors.textSubtle};
`;

const BreadcrumbButton = styled(UnstyledLink)`
  cursor: pointer;
  color: ${colors.textSubtle};
  &:focus-visible {
    ${getFocusRingBorderCSS()}
  }
`;

export type NewPageTopBarProps = {
  toggleSidebarButton: ToggleSidebarButtonProps | null;
  breadcrumbs: BreadcrumbItem[];
  actionItems: ActionMenuItem[];
  helpButtonItems?: HelpButtonItem[];
  badges?: (ReadonlyBadgeProps | InteractiveBadgeProps)[];
  trailingButton?: TrailingButton;
  className?: string;
  i18nLocale?: I18nLocale;
};

export const toTopBarIconButtonProps = ({
  buttonType,
  ...action
}: CommonActionMenuItemProps & {
  onClick: VoidFunction;
  icon: {
    name: IconName;
  };
}): ActionMenuIconButton => {
  return {
    type: 'icon-button',
    buttonType: buttonType ?? ButtonType.GHOST,
    ...action,
  };
};

export const toTopBarDropdownProps = ({
  buttonType,
  ...action
}: CommonActionMenuItemProps & {
  isIconOnly?: boolean;
  options: DropdownItem[];
  maxDropdownSize?: DropdownSize;
  onMenuOpen?: VoidFunction;
}): ActionMenuDropdownButton => {
  return {
    type: 'dropdown',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarActionButtonProps = ({
  buttonType,
  ...action
}: CommonActionMenuItemProps & {
  onClick: VoidFunction;
  tooltipText?: string;
}): ActionMenuButton => {
  return {
    type: 'button',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarActionButtonPropsReturningActions = ({
  buttonType,
  ...action
}: PropsReturningActions<
  CommonActionMenuItemProps & {
    onClick: VoidFunction;
    tooltipText?: string;
  }
>): PropsReturningActions<ActionMenuButton> => {
  return {
    type: 'button',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarDropdownPropsReturningActions = ({
  buttonType,
  ...action
}: PropsReturningActions<
  CommonActionMenuItemProps & {
    isIconOnly?: boolean;
    options: DropdownItem[];
    maxDropdownSize?: DropdownSize;
    onMenuOpen?: VoidFunction;
  }
>): PropsReturningActions<ActionMenuDropdownButton> => {
  return {
    type: 'dropdown',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarLinkButtonProps = ({
  ...action
}: {
  label: string;
  href: string;
  onClick: VoidFunction;
  icon: {
    name: IconName;
  };
}): ActionMenuLinkButton => ({
  type: 'link',
  target: '_self',
  ...action,
});

export const toTopBarActionDividerProps = (): ActionMenuDivider => ({
  type: 'divider',
});

const HelpDropdown = ({
  items,
  i18nLocale,
}: {
  items: HelpButtonItem[];
  i18nLocale: I18nLocale;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useDropdownTriggerContainer<HTMLButtonElement>({
    isOpen,
    openDropdown: () => setIsOpen(true),
  });
  const popoverId = useId();

  return (
    <>
      <IconButton
        ref={triggerRef}
        label={t(
          i18nLocale,
          'pageLayout:newPageTopBar.helpButtonLabel',
          'Help'
        )}
        iconName={IconName.HELP}
        isActive={isOpen}
        onClick={() => setIsOpen(open => !open)}
        aria-controls={popoverId}
        aria-expanded={isOpen}
      />
      <Popover2025
        id={popoverId}
        isOpen={isOpen}
        anchorRef={triggerRef}
        onClose={() => setIsOpen(false)}
        onClickOutside={() => setIsOpen(false)}
        placement={{ side: 'bottom', overflow: 'right' }}
      >
        <DropdownContainer dropdownMaxHeight={DropdownMaxHeight.AUTO}>
          <DropdownMenu>
            {items.map((item, index) => {
              if (item.type === 'header') {
                return (
                  <DropdownLabel key={index} variant="header">
                    {item.label}
                  </DropdownLabel>
                );
              }
              if (item.type === 'link') {
                return (
                  <DropdownLink
                    key={index}
                    href={item.href}
                    onClick={item.onClick}
                    onTrack={item.onTrack}
                    target={item.target}
                  >
                    <DropdownOptionContent
                      icon={item.icon}
                      label={item.label}
                      description={item.description}
                    />
                  </DropdownLink>
                );
              }
              return (
                <DropdownOption
                  key={index}
                  onClick={() => {
                    setIsOpen(false);
                    item.onTrack?.();
                    item.onClick();
                  }}
                >
                  <DropdownOptionContent
                    icon={item.icon}
                    label={item.label}
                    description={item.description}
                  />
                </DropdownOption>
              );
            })}
          </DropdownMenu>
        </DropdownContainer>
      </Popover2025>
    </>
  );
};

export const NewPageTopBar = ({
  toggleSidebarButton,
  breadcrumbs,
  actionItems,
  helpButtonItems = [],
  badges = [],
  trailingButton,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: NewPageTopBarProps) => {
  return (
    <TopBarWrapper
      role="banner"
      /** Todo: Change to medium */
      paddingX="large"
      align="center"
      backgroundColor="surfaceDefault"
      minHeight={`${NEW_PAGE_TOP_BAR_HEIGHT}px`}
      justify="space-between"
      width="full"
      /** Needed to render above open grid editor */
      zIndex={1}
    >
      <FlexBox gap="small" align="center">
        {toggleSidebarButton !== null && (
          <IconButton
            label={t(
              i18nLocale,
              'pageLayout:newPageTopBar.openMenu',
              'Open menu'
            )}
            buttonSize={ButtonSize.SMALL}
            iconName={IconName.SIDEBAR_OPEN}
            onClick={toggleSidebarButton.onClick}
            onMouseEnter={toggleSidebarButton.onHover}
          />
        )}

        <FlexBox
          gap="small"
          align="center"
          as="nav"
          aria-label={t(
            i18nLocale,
            'pageLayout:newPageTopBar.breadcrumbAriaLabel',
            'Breadcrumb'
          )}
        >
          <Inline gap="xsmall" as="ul" align="center">
            {breadcrumbs.map((breadcrumb, index) => (
              <FlexBox
                key={index}
                id={breadcrumb.htmlId}
                gap="xsmall"
                align="center"
                as="li"
                aria-current={
                  index === breadcrumbs.length - 1 ? 'page' : undefined
                }
              >
                {index > 0 && (
                  <BreadcrumbSeparator variant="text2" aria-hidden="true">
                    /
                  </BreadcrumbSeparator>
                )}
                {index < breadcrumbs.length - 1 ? (
                  <BreadcrumbButton
                    onClick={breadcrumb.onClick}
                    href={breadcrumb.href}
                  >
                    <Text variant="text2">{breadcrumb.label}</Text>
                  </BreadcrumbButton>
                ) : (
                  <Text variant="text2Bold">{breadcrumb.label}</Text>
                )}
              </FlexBox>
            ))}
          </Inline>
          <FlexBox gap="xsmall">
            {badges.map((badge, index) => (
              <Badge key={index} {...badge} />
            ))}
          </FlexBox>
        </FlexBox>
        {trailingButton && (
          <SmallGhostButton onClick={trailingButton.onClick}>
            <Icon
              iconName={trailingButton.icon.name}
              iconSize={IconSize.SMALL}
            />
            {trailingButton.label}
          </SmallGhostButton>
        )}
      </FlexBox>

      <Right align="center" justify="flex-end" gap="small">
        {actionItems.map((action, index) => (
          <NewPageTopBarActionButton key={index} action={action} />
        ))}
        {helpButtonItems.length > 0 && (
          <HelpDropdown items={helpButtonItems} i18nLocale={i18nLocale} />
        )}
      </Right>
    </TopBarWrapper>
  );
};
